/**
 * UranIA PA — Stepper riusabile (US-015 AC#1 PA, US-016 R&I).
 *
 * Stepper Bootstrap Italia 2.x: indica al Funzionario / Beneficiario lo
 * step corrente sui 5 totali con label italian-first + stato (completato,
 * corrente, futuro) accessibile via `aria-current="step"` sull'item attivo
 * (WAI-ARIA Authoring Practices "step indicator" pattern, allineato a
 * design.italia.it `Stepper`).
 *
 * Rendering:
 *   - <ol> ordinato (semantics: sequenza ordinata di passi);
 *   - ogni <li> ha numero (visivo) + label + stato; lo step corrente porta
 *     `aria-current="step"` per gli screen reader;
 *   - barra di progresso visibile per dare context immediato anche a chi
 *     usa zoom alto / dispositivi piccoli.
 *
 * A11y: WCAG 2.1 §2.4.6 (Headings/Labels), §1.3.1 (Info & Relationships).
 * Verificato da axe-core WCAG2 AA (vedi `spa-onboarding-pa-axe.spec.ts`).
 */

import type { JSX } from 'react';

export interface OnboardingStepperStep {
  readonly number: number;
  readonly label: string;
  readonly description?: string;
}

export interface OnboardingStepperProps {
  readonly steps: readonly OnboardingStepperStep[];
  /** 1-based; 0 significa "nessuno step ancora avviato". */
  readonly currentStep: number;
  /** label `aria-label` per il landmark <nav>. */
  readonly ariaLabel: string;
  /** Etichetta accessibile per il counter "Step N di M". */
  readonly counterLabel: (current: number, total: number) => string;
}

export function OnboardingStepper(props: OnboardingStepperProps): JSX.Element {
  const { steps, currentStep, ariaLabel, counterLabel } = props;
  const total = steps.length;
  const safeCurrent = Math.max(1, Math.min(currentStep, total));
  const progressPct = Math.round((safeCurrent / total) * 100);

  return (
    <nav
      className="urania-onboarding-stepper mb-4"
      aria-label={ariaLabel}
      data-testid="onboarding-stepper"
    >
      <p className="visually-hidden" data-testid="onboarding-stepper-counter">
        {counterLabel(safeCurrent, total)}
      </p>
      <ol className="urania-onboarding-stepper__list list-unstyled d-flex flex-wrap gap-2 mb-2">
        {steps.map((step) => {
          const isCompleted = step.number < safeCurrent;
          const isCurrent = step.number === safeCurrent;
          const stateClass = isCurrent
            ? 'urania-onboarding-stepper__item--current'
            : isCompleted
              ? 'urania-onboarding-stepper__item--completed'
              : 'urania-onboarding-stepper__item--upcoming';
          return (
            <li
              key={step.number}
              className={`urania-onboarding-stepper__item ${stateClass}`}
              aria-current={isCurrent ? 'step' : undefined}
              data-testid={`onboarding-stepper-item-${String(step.number)}`}
            >
              <span className="urania-onboarding-stepper__badge" aria-hidden="true">
                {step.number}
              </span>
              <span className="urania-onboarding-stepper__label">{step.label}</span>
            </li>
          );
        })}
      </ol>
      <div
        className="progress"
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressPct}
      >
        <div
          className="progress-bar"
          style={{ width: `${String(progressPct)}%` }}
          data-testid="onboarding-stepper-progress"
        />
      </div>
    </nav>
  );
}
